import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import { CheckCircle2, Download, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate } from "../lib/api";

export default function BillingView() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);

  const load = () => api.get("/master/subscription-invoices").then(({ data }) => setInvoices(data)).catch(() => toast.error("Could not load invoices")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const paid = invoices.filter((i) => i.status === "paid");
    const unpaid = invoices.filter((i) => i.status !== "paid");
    return {
      collected: paid.reduce((s, i) => s + (i.total || 0), 0),
      outstanding: unpaid.reduce((s, i) => s + (i.total || 0), 0),
      paidCount: paid.length, unpaidCount: unpaid.length,
    };
  }, [invoices]);

  const shown = invoices.filter((i) => filter === "all" || i.status === filter);

  const runBilling = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/master/billing/run");
      toast.success(data.invoices_created ? `Generated ${data.invoices_created} due invoice(s)` : "No invoices due right now");
      load();
    } catch { toast.error("Could not run billing"); } finally { setRunning(false); }
  };

  const setPaid = async (inv, paid) => {
    try {
      await api.post(`/master/subscription-invoices/${inv.id}/mark-${paid ? "paid" : "unpaid"}`);
      toast.success(paid ? "Marked as paid" : "Marked as unpaid");
      load();
    } catch { toast.error("Could not update invoice"); }
  };

  const downloadPDF = (inv) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const left = 48; let y = 60;
    doc.setFillColor(10, 22, 40); doc.rect(0, 0, 595, 96, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
    doc.text("FlowSphere System", left, 52);
    doc.setFontSize(10); doc.setTextColor(160, 190, 220); doc.setFont("helvetica", "normal");
    doc.text("Subscription Invoice", left, 72);
    doc.setTextColor(10, 22, 40); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(inv.invoice_number || "", 595 - left, 52, { align: "right" });
    y = 140;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text("Billed to", left, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11);
    doc.text(inv.client_name || "Client", left, y + 18);
    if (inv.client_email) doc.text(inv.client_email, left, y + 34);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90, 107, 132);
    doc.text(`Issue date: ${shortDate(inv.issue_date)}`, 595 - left, y, { align: "right" });
    doc.text(`Due date: ${shortDate(inv.due_date)}`, 595 - left, y + 16, { align: "right" });
    doc.text(`Status: ${(inv.status || "").toUpperCase()}`, 595 - left, y + 32, { align: "right" });
    y += 80;
    doc.setDrawColor(225, 232, 240); doc.line(left, y, 595 - left, y); y += 24;
    doc.setTextColor(10, 22, 40); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Description", left, y); doc.text("Amount", 595 - left, y, { align: "right" }); y += 8;
    doc.line(left, y, 595 - left, y); y += 24;
    doc.setFont("helvetica", "normal");
    doc.text(`${inv.package_name || "Subscription"} — ${inv.interval || ""} plan`, left, y);
    doc.text(money(inv.base || 0, "INR"), 595 - left, y, { align: "right" }); y += 22;
    doc.setTextColor(90, 107, 132);
    doc.text("GST (18%)", left, y); doc.text(money(inv.gst || 0, "INR"), 595 - left, y, { align: "right" }); y += 12;
    doc.line(left, y, 595 - left, y); y += 26;
    doc.setTextColor(10, 22, 40); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("Total", left, y); doc.text(money(inv.total || 0, "INR"), 595 - left, y, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(150, 165, 185);
    doc.text("Thank you for subscribing to FlowSphere System.", left, 800);
    doc.save(`${inv.invoice_number || "invoice"}.pdf`);
  };

  return (
    <section className="panel module-panel" data-testid="billing-view">
      <div className="admin-stat-grid" style={{ marginBottom: 18 }}>
        <div className="admin-stat-card" style={{ cursor: "default" }} data-testid="billing-collected"><span className="admin-stat-value">{money(stats.collected, "INR")}</span><span className="admin-stat-label">Collected</span><span className="admin-stat-hint">{stats.paidCount} paid invoices</span></div>
        <div className="admin-stat-card" style={{ cursor: "default" }} data-testid="billing-outstanding"><span className="admin-stat-value">{money(stats.outstanding, "INR")}</span><span className="admin-stat-label">Outstanding</span><span className="admin-stat-hint">{stats.unpaidCount} unpaid invoices</span></div>
      </div>

      <div className="module-toolbar">
        <div className="filter-tabs">
          {["all", "unpaid", "paid"].map((f) => (
            <button key={f} className={`filter-tab ${filter === f ? "active" : ""}`} data-testid={`billing-filter-${f}`} onClick={() => setFilter(f)}>{f[0].toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <button className="secondary-button" data-testid="run-billing-button" disabled={running} onClick={runBilling}><RefreshCw size={15} className={running ? "spin" : ""} /> {running ? "Running…" : "Generate due invoices"}</button>
      </div>

      {loading ? <div className="admin-loading">Loading invoices…</div> : shown.length === 0 ? (
        <div className="empty-state"><h3>No invoices yet</h3><p>Subscription invoices from paid signups and renewals will appear here.</p></div>
      ) : (
        <div className="table-wrap">
          <table data-testid="billing-table">
            <thead><tr><th>Invoice</th><th>Client</th><th>Plan</th><th>Total</th><th>Status</th><th>Due</th><th>Actions</th></tr></thead>
            <tbody>
              {shown.map((inv) => (
                <tr key={inv.id} data-testid={`billing-row-${inv.id}`}>
                  <td>{inv.invoice_number}</td>
                  <td>{inv.client_name || "—"}<small>{inv.client_email}</small></td>
                  <td>{inv.package_name} · {inv.interval}</td>
                  <td>{money(inv.total || 0, "INR")}<small>incl. GST</small></td>
                  <td><span className={`plan-pill ${inv.status === "paid" ? "pro" : "basic"}`}>{inv.status === "paid" ? "Paid" : "Unpaid"}</span></td>
                  <td>{shortDate(inv.due_date)}</td>
                  <td>
                    <div className="client-row-actions">
                      {inv.status === "paid"
                        ? <button className="link-button" data-testid={`unpay-${inv.id}`} onClick={() => setPaid(inv, false)}><Undo2 size={14} /> Mark unpaid</button>
                        : <button className="link-button accent" data-testid={`pay-${inv.id}`} onClick={() => setPaid(inv, true)}><CheckCircle2 size={14} /> Mark paid</button>}
                      <button className="link-button" data-testid={`pdf-${inv.id}`} onClick={() => downloadPDF(inv)}><Download size={14} /> PDF</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
